import { type SchedulerPro } from '@bryntum/schedulerpro'
import { produce } from 'immer'
import { type ReactNode, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { useMainSelection } from '@/atoms'
import { selectUserAccountPreferences } from '@/features/domain/user'

import { schedulerInstance } from '../../../../atoms/project/project'
import { useSchedulerPreferencesAtom } from '../../../../atoms/schedulerPreferences'
import { checkAllExpanded } from '../../utils/checkAllExpanded'
import { SchedulerHeader } from './SchedulerHeader'
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts'
import { useOnZoomRoutes } from './hooks/useOnZoomRoutes'
import { useResetColumnInfo } from './hooks/useResetColumnsInfo'
import { useRouteStats } from './hooks/useRouteStats'

interface Props {
  scheduler?: SchedulerPro
  editColumns: () => void
  ResizeHandle?: ReactNode
}

export function SchedulerHeaderProvider(props: Props) {
  const { editColumns, ResizeHandle } = props

  const [allExpanded, setAllExpanded] = useState<boolean>(true)
  const [schedulerPreferences, setSchedulerPreferences] = useSchedulerPreferencesAtom()
  const [selectedRoutes, setSelectedRoutes] = useMainSelection('routes')
  const accountPreferences = useSelector(selectUserAccountPreferences)
  const resetColumnInfo = useResetColumnInfo()
  const onZoomRoutes = useOnZoomRoutes()
  const routeStats = useRouteStats()

  // Keyboard shortcuts for expanding and collapsing all groups
  useKeyboardShortcuts({ schedulerInstance, setAllExpanded })

  const { routeIds, unavailableRouteIds } = routeStats
  const { showUnavailableRoutes } = schedulerPreferences

  const routesCounter = showUnavailableRoutes
    ? routeIds.length
    : routeIds.length - unavailableRouteIds.length

  const allSelected = routesCounter === selectedRoutes.length

  const handleSetUnitMode = useCallback(
    (unit: 'percentage' | 'absolute') => {
      setSchedulerPreferences(prev =>
        produce(prev, draft => {
          draft.showAbsoluteValues = unit === 'absolute'
        }),
      )
    },
    [setSchedulerPreferences],
  )

  const setShowApprovedRoutes = useCallback(
    (value: boolean) => {
      setSchedulerPreferences(prev =>
        produce(prev, draft => {
          draft.showApprovedRoutes = value
        }),
      )
    },
    [setSchedulerPreferences],
  )

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedRoutes([])
      return
    }

    setSelectedRoutes(
      showUnavailableRoutes ? routeIds : routeIds.filter(r => !unavailableRouteIds.includes(r)),
    )
  }, [setSelectedRoutes, showUnavailableRoutes, unavailableRouteIds, allSelected, routeIds])

  const toggleExpandCollapseAll = () => {
    allExpanded
      ? schedulerInstance.instance?.collapseAll()
      : schedulerInstance.instance?.expandAll()
    setAllExpanded(!allExpanded)
  }

  const removeUnavailableRoutesFromSelection = useCallback(() => {
    setSelectedRoutes(selectedRoutes.filter(r => !unavailableRouteIds.includes(r)))
  }, [setSelectedRoutes, selectedRoutes, unavailableRouteIds])

  const setGroupBy = useCallback(
    (groupBy: 'dateAsString' | 'vehicleId') => {
      setAllExpanded(true)
      schedulerInstance.instance?.expandAll()

      setSchedulerPreferences(prev =>
        produce(prev, draft => {
          draft.groupBy = groupBy
        }),
      )
    },
    [setSchedulerPreferences],
  )

  const setShowUnavailableRoutes = useCallback(
    (show: boolean) => {
      setSchedulerPreferences(prev =>
        produce(prev, draft => {
          draft.showUnavailableRoutes = show
        }),
      )
    },
    [setSchedulerPreferences],
  )

  // Check if all groups are expanded when a group is toggled
  schedulerInstance.instance?.on('toggleGroup', () => {
    setAllExpanded(checkAllExpanded())
  })

  return (
    <SchedulerHeader
      groupBy={schedulerPreferences.groupBy}
      unitMode={schedulerPreferences.showAbsoluteValues ? 'absolute' : 'percentage'}
      routeStats={routeStats}
      setGroupBy={setGroupBy}
      setAllExpanded={setAllExpanded}
      allSelected={allSelected}
      editColumns={editColumns}
      setUnitMode={handleSetUnitMode}
      ResizeHandle={ResizeHandle}
      onZoomRoutes={onZoomRoutes}
      routesCounter={routesCounter}
      selectedRoutes={selectedRoutes}
      toggleSelectAll={toggleSelectAll}
      toggleExpandCollapseAll={toggleExpandCollapseAll}
      allExpanded={allExpanded}
      accountPreferences={accountPreferences}
      showApprovedRoutes={setShowApprovedRoutes}
      approvedRoutesVisible={schedulerPreferences.showApprovedRoutes}
      setShowUnavailableRoutes={setShowUnavailableRoutes}
      resetSchedulerColumnsInfo={resetColumnInfo}
      areUnavailableRoutesVisible={showUnavailableRoutes}
      removeUnavailableRoutesFromSelection={removeUnavailableRoutesFromSelection}
    />
  )
}
